import * as R from "ramda";
import analytics from "../analytics";

/*
STATIC PROGRAM VALUES
*/
const portfolioIncentiveAmounts2024 = {
  0: 1,
  1: 1,
  2: 3,
  3: 4,
  4: 5,
  5: 6,
};

const portfolioIncentiveAmounts2025 = {
  0: 1,
  1: 1,
  2: 3,
  3: 4,
  4: 5,
};

const portfolioIncentiveTypes = {
  "seed": 1,
  "herbicide": 2,
  "fungicide": 3,
  "insecticide": 4,
  "nematicide": 5
}

const portfolioProductCap5 = 5;
const portfolioProductCap6 = 6;
const xtendimaxIncentiveValue = 2;
const partnerProductsIncentiveValue2024 = 2;
const partnerProductsFierceIncentiveValue2025 = 2;
const partnerProductsIncentiveValue2025 = 1;
const partnerProductsRoundupIncentiveValue = 1;
const additionalIncentiveValue = 0.5;

/** FROM getter.js IN OLD APP */
const step1Products = (state) => {
  let allStep1Products = R.flatten([
    state.step1Seeds,
    state.step1Herbicides,
    state.step1Fungicides,
    state.step1Insecticides,
    state.step1Nematicides,
  ]);

  let allPairProducts = R.compose(R.values)(
    R.mergeAll([
      state.step1PairSeeds,
      state.step1PairHerbicides,
      state.step1PairFungicides,
      state.step1PairInsecticides,
      state.step1PairNematicides,
    ])
  );

  allStep1Products = R.union(allStep1Products, allPairProducts);

  return allStep1Products;
};
const step1IncentiveEligible = (state) => {
  var seedSelected =
    R.filter((item) => {
      return item.product_type === "seed" && canBeCalculated(item);
    }, R.flatten(step1Products(state))).length > 0;

  var otherSelections = R.filter((item) => {
    return item.product_type !== "seed" && canBeCalculated(item);
  }, R.flatten(step1Products(state)));

  return (
    otherSelections.length >= 2 || (seedSelected && otherSelections.length >= 1)
  );
};

const step4Eligible = (state) => {
  return (
    R.filter((item) => {
      return canBeCalculated(item);
    }, R.flatten(step1Products(state))).length > 0
  );
};
const herbicidesSelected = (state) => {
  return (
    R.filter((item) => {
      return item.product_type === "herbicide" && canBeCalculated(item);
    }, R.flatten(step1Products(state))).length > 0
  );
};

const roundupSelectedWithQuantity = (state) => {
  return (
    R.filter((item) => {
      return canBeCalculated(item);
    }, state.step2Roundup).length > 0
  );
};

const roundupIncentiveEligible = (state) => {
  //2022 must have at least one herbicide selected with a quantity entered
  return herbicidesSelected(state);
};

const xtendimaxIncentiveEligible = (state) => {
  // 2022 no Roundup selection required to unlock and calculate xtendimaxIncentive
  return true;
};
// must be earning incentives from either step 1 or step 2
// (either two step 1 products selected or 1 herbicide and 1 roundup)
const additionalIncentiveEligible = (state) => {
  return step1IncentiveEligible(state) || roundupSelectedWithQuantity(state);
};

const partnerProductsIncentiveEligible = (state) => {
  return (
    step1IncentiveEligible(state) ||
    (herbicidesSelected(state) && roundupSelectedWithQuantity(state))
  );
};
const loyaltyRewardsToRetailer = (state) => {
  return state.loyaltyRewardsToRetailer;
};
const loyaltyRewards = (state) => {
  if (state.loyaltyRewardsToRetailer > 0) {
    return state.loyaltyRewardsToRetailer * 0.1;
  } else {
    return 0;
  }
};
const totalRewardsToRetailer = (state) => {
  if (state.loyaltyRewardsToRetailer > 0) {
    return (
      state.loyaltyRewardsToRetailer + state.loyaltyRewardsToRetailer * 0.1
    );
  } else {
    return 0;
  }
};
const comments = (state) => {
  return state.comments;
};
const pdfRequest = (state) => {
  return generatePdf(state);
};
/*****************************/
/*
ACREAGE
*/

function productSetAcres(productUnitsType, productSet) {
  var destructured = R.map((item) => {
    return destructureProduct(Object.assign({}, item), productUnitsType);
  }, productSet);

  return R.sum(
    R.map((item) => {
      return item.acres;
    }, destructured)
  );
}
/*
INCENTIVES
*/
function seedIncentive(productUnitsType, portfolioProducts, year) {
  // get standardized product objects
  if (portfolioProducts && portfolioProducts.length) {
    var sorted = getSortedArrayHelper(productUnitsType, portfolioProducts);
    var seedIndex = sorted.findIndex((item) => item.productType === "seed");

    if (seedIndex !== -1) {
      if (seedIndex === 1) {
        if (year === 2024) {
          return (
            sorted[0].acres * portfolioIncentiveAmounts2024[0] +
            sorted[1].acres * portfolioIncentiveAmounts2024[1]
          );
        }
        if (year === 2025) {
          return (
            sorted[0].acres * portfolioIncentiveAmounts2025[0] +
            sorted[1].acres * portfolioIncentiveAmounts2025[1]
          );
        }
        return 0;
      } else if (seedIndex === 0) {
        return -1;
      } else {
        if (year === 2024) {
          return sorted[seedIndex].acres * portfolioIncentiveAmounts2024[seedIndex];
        }
        if (year === 2025) {
          return sorted[seedIndex].acres * portfolioIncentiveAmounts2025[seedIndex];
        }
        return 0;
      }
    }
  } else {
    return 0;
  }
}

function portfolioIncentive(productUnitsType, portfolioProducts, year) {
  // get standardized product objects
  if (portfolioProducts && portfolioProducts.length) {
    var sorted = getSortedArrayHelper(productUnitsType, portfolioProducts);
    //2024 calculation
    if (year === "2024") {
      var accumulator = R.map((item) => {
        return item.acres * portfolioIncentiveAmounts2024[sorted.indexOf(item)];
      }, R.take(portfolioProductCap6)(sorted)); // only pull incentive values up to the maximum number of products
  
      return R.sum(accumulator);
    }

    if (year === "2025") {
      var accumulator = R.map((item) => {
        return item.acres * portfolioIncentiveAmounts2025[sorted.indexOf(item)];
      }, R.take(portfolioProductCap5)(sorted)); // only pull incentive values up to the maximum number of products
  
      return R.sum(accumulator);
    }

    return 0;
  } else {
    return 0;
  }
}

function getQualifyingProductType(productUnitsType, portfolioProducts) {
  if (portfolioProducts && portfolioProducts.length) {
    var sorted = getSortedArrayHelper(productUnitsType, portfolioProducts);
    if (sorted && sorted.length > 0 && sorted[0].acres > 0) {
      return sorted[0].productType;
    } else {
      return "";
    }
  } else {
    return "";
  }
}

function isQualifyingProductLast(productUnitsType, portfolioProducts) {
  if (portfolioProducts && portfolioProducts.length) {
    var sorted = getSortedArrayHelper(productUnitsType, portfolioProducts);
    if (sorted && sorted[0].acres > 0) {
      var qualifyingProductType = sorted[0].productType;
      var qpValue = portfolioIncentiveTypes[qualifyingProductType]
        for (var i = 1; i < sorted.length; i++) {
          var itemProductType = sorted[i].productType;
        
          if (sorted[i].acres > 0) {
            if (qpValue < portfolioIncentiveTypes[itemProductType]) {
              return false;
            }
          }
        }
        return true;
    } 
  }
  return false
}

function roundupIncentive(
  productUnitsType,
  roundupProducts,
  step1Herbicides,
  year
) {
  var roundupIncentiveValue = 1;
  if (roundupProducts.length) {
    // do roundup calc based on herbicides acerage
    var incentivizedAcreage = "";

    incentivizedAcreage = R.min(
      productSetAcres(productUnitsType, roundupProducts),
      productSetAcres(productUnitsType, step1Herbicides)
    );

    return incentivizedAcreage * roundupIncentiveValue;
    // do roundup calc based on incentives acerage
  } else {
    return 0;
  }
}
function xtendimaxIncentive(
  productUnitsType,
  xtendimaxProducts,
  step1Herbicides,
  roundupProducts,
  year
) {
  if (xtendimaxProducts.length) {
    var incentivizedAcreage = R.min(
      productSetAcres(productUnitsType, xtendimaxProducts),
      productSetAcres(productUnitsType, step1Herbicides)
    
    );
    return incentivizedAcreage; //2022 multiplier is 1x
  } else {
    return 0;
  }
}
// currently additional incentives have no capping step
function additionalIncentive(productUnitsType, additionalProducts) {
  if (additionalProducts.length) {
    return (
      productSetAcres(productUnitsType, additionalProducts) *
      additionalIncentiveValue
    );
  } else {
    return 0;
  }
}
function partnerProductIncentive(state) {
  return state.step4Products.length && this.partnerProductsIncentiveEligible
    ? partnerProductsIncentive(
        state.productUnitsType,
        state.step1AllProducts,
        state.step2Roundup,
        state.step4Products,
        state.year
      )
    : 0;
}
function partnerProductsIncentive(
  productUnitsType,
  allStep1Products,
  roundupProducts,
  partnerProducts,
  year
) {
  if (
    partnerProducts &&
    allStep1Products &&
    partnerProducts.length &&
    allStep1Products.length
  ) {
    var partnerProductIncentiveVal = 0;
    if (year === "2024") {
      partnerProductIncentiveVal = productSetAcres(productUnitsType, partnerProducts) *
        partnerProductsIncentiveValue2024;
    }
    if (year === "2025") {
      var partnerProductsArr = partnerProducts.map(product => ({... product}));
      var fiercePartnerProdusctsArr = [];
      for (var i = partnerProductsArr.length - 1; i >= 0; i--) {
        if (partnerProductsArr[i].title && partnerProductsArr[i].title.includes("fierce")) {
          var fierceProduct = partnerProductsArr.splice(i, 1);
          fiercePartnerProdusctsArr.push(fierceProduct[0]);
        } 
      }
      if (partnerProductsArr !== undefined && partnerProductsArr.length != 0) {
        partnerProductIncentiveVal += (productSetAcres(productUnitsType, partnerProductsArr) *
        partnerProductsIncentiveValue2025);
      }
      if (fiercePartnerProdusctsArr !== undefined && fiercePartnerProdusctsArr.length != 0) {
        partnerProductIncentiveVal += (productSetAcres(productUnitsType, fiercePartnerProdusctsArr) *
        partnerProductsFierceIncentiveValue2025);
      }
    }
    return partnerProductIncentiveVal;
  } else {
    return 0;
  }
}
function getSortedArrayHelper(productUnitsType, portfolioProducts) {
  var destructured = R.compose(
    R.filter((x) => R.prop("excluded", x) === false),
    R.map((item) => {
      if (R.prop("product_type", item)) {
        return destructureProduct(item, productUnitsType);
      } else if (item[0] && item[1]) {
        var prodA = destructureProduct(item[0], productUnitsType);
        var prodB = destructureProduct(item[1], productUnitsType);
        return Object.assign({}, prodA, {
          acres: R.add(prodA.acres, prodB.acres),
        });
      }
    })
  )(portfolioProducts);

  // combine all seed acreage values (seed should be treated as one "product")
  var seedAcreage = R.sum(
    R.map(
      (item) => {
        return item.acres;
      },
      R.filter((item) => {
        return item.productType === "seed";
      }, destructured)
    )
  );

  // add the seed acreage as a single entity back to the non-seed product array
  var calcArray = R.append(
    {
      productType: "seed",
      acres: seedAcreage,
    },
    R.filter((item) => {
      return item.productType !== "seed";
    }, destructured)
  );

  // sort highest to lowest acreage
  var sorted = R.sort(R.descend(R.prop("acres")))(calcArray);

  // reset acreage of top two products as smaller common acreage of those two products
  if (sorted.length > 1) {
    var firstSecondAcreage = R.min(sorted[0].acres, sorted[1].acres);
    sorted[0].acres = firstSecondAcreage;
    sorted[1].acres = firstSecondAcreage;
  }


  return sorted;
}
/*
MISC
*/
function toggleAccordion(e) {
  let heading = e.currentTarget;
  heading.classList.toggle("collapsed");
}
// for filtering out products than have undefined quantities or quantity of zero
function hasQuantity(product) {
  return (
    R.has("_userQuantity", product) &&
    !R.isNil(R.prop("_userQuantity")(product)) &&
    product._userQuantity !== 0
  );
}

function hasSelection(product) {
  return R.has("unit_of_measure", product) && R.has("match_rate", product);
}

function canBeCalculated(product) {
  return hasSelection(product) && hasQuantity(product);
}
function titleCase(str) {
  return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
}

function sum(amounts) {
  return R.sum(amounts);
}
function format(amount) {
  return formatWholeNumber(amount);
}
function formatOneDecimal(amount) {
  return amount % 1 !== 0
    ? parseFloat(Number(amount.toFixed(1)).toString())
    : parseFloat(amount.toString());
}

function formatTwoDecimals(amount) {
  return amount % 1 !== 0
    ? parseFloat(Number(amount.toFixed(2)).toString())
    : parseFloat(amount.toString());
}

function formatWholeNumber(amount) {
  return Math.round(amount).toLocaleString();
}

function formatWholeNumberToNumber(amount) {
  return Math.round(amount);
}

// rounding to 2 decimal places
function roundValue(value) {
  return Number(Math.round(value + "e2") + "e-2");
}

function formatSeedingRate(rate, unitOfMeasure) {
  var rateStr = rate.toString();

  // handle seeding rates displayed as "seeds" or "lbs/acre"
  if (unitOfMeasure === "seeds") {
    return `${rateStr.substring(0, rateStr.length - 3)}k ${unitOfMeasure}`;
  } else if (unitOfMeasure === "seed lbs") {
    return `${rateStr} lbs/acre`;
  }
}

/*
CONVERSIONS
*/
function convertUnitsToGallons(unit) {
  // R.cond evaluates the first function and returns the second function when a match is found
  return R.cond([
    [() => unit === "fl oz", (amount) => amount / 128],
    [() => unit === "oz", (amount) => amount / 16],
    [() => unit === "pts", (amount) => amount / 8],
    [() => unit === "qts", (amount) => amount / 4],
  ]);
}

function userQuantityDisplayUnits(unit, productType) {
  var galUnits = ["fl oz", "pts", "qts"];
  var lbsUnits = ["oz"];
  var bagsUnits = ["seeds", "seed lbs"];
  var casesUnits = ["cases"];

  return R.cond([
    [() => R.contains(unit, galUnits), () => "gal"],
    [() => R.contains(unit, lbsUnits), () => "lbs"],
    [
      () => R.contains(unit, bagsUnits) && productType === "additional",
      () => "bushels",
    ],
    [() => R.contains(unit, bagsUnits), () => "bags"],
    [() => R.contains(unit, casesUnits), () => "cases"],
  ]);
}

// get acres for single product
function productAcres(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (unitOfMeasure, unitsPerAcre, userQuantity) => Number(userQuantity),
    ],
    [
      () => productUnitsType === "volume",
      (unitOfMeasure, unitsPerAcre, userQuantity) =>
        Number(
          userQuantity / convertUnitsToGallons(unitOfMeasure)(unitsPerAcre)
        ),
    ],
  ]);
}

// get volume for single product
function productVolume(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (unitOfMeasure, unitsPerAcre, userQuantity) =>
        Number(
          userQuantity * convertUnitsToGallons(unitOfMeasure)(unitsPerAcre)
        ),
    ],
    [
      () => productUnitsType === "volume",
      (unitOfMeasure, unitsPerAcre, userQuantity) => Number(userQuantity),
    ],
  ]);
}

// leaving seed acreage as a separate function rather than refactoring in case it gets changed again
// TODO: look at refactoring after this goes live
function seedBagsPerAcre(acresPerUnit) {
  return acresPerUnit;
}

// if seed quantity entered in acres ('acre' selected at app start)
function seedAcres(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (acresPerUnit, userQuantity) => Number(userQuantity),
    ],
    [
      () => productUnitsType === "volume",
      (acresPerUnit, userQuantity) =>
        Number(userQuantity * seedBagsPerAcre(acresPerUnit)),
    ],
  ]);
}

// if seed quantity entered in bags ('volume' selected at app start)
function seedVolume(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (acresPerUnit, userQuantity) =>
        Number(userQuantity / seedBagsPerAcre(acresPerUnit)),
    ],
    [
      () => productUnitsType === "volume",
      (acresPerUnit, userQuantity) => Number(userQuantity),
    ],
  ]);
}

// cases currently have a different conversion mechanism than other units of measure
function caseAcres(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (unitsPerAcre, userQuantity) => Number(userQuantity),
    ],
    [
      () => productUnitsType === "volume",
      (unitsPerAcre, userQuantity) => Number(userQuantity / unitsPerAcre),
    ],
  ]);
}

function caseVolume(productUnitsType) {
  return R.cond([
    [
      () => productUnitsType === "acre",
      (unitsPerAcre, userQuantity) => Number(userQuantity * unitsPerAcre),
    ],
    [
      () => productUnitsType === "volume",
      (unitsPerAcre, userQuantity) => Number(userQuantity),
    ],
  ]);
}

// consolidate conversion mechanisms, keyword designates which function will be returned
// requires all six params be passed because different conversions require different vars
function standardizedAcreage(keyword) {
  return R.cond([
    [
      () => keyword === "cases",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) => caseAcres(productUnitsType)(unitsPerAcre, userQuantity),
    ],
    [
      () => keyword === "seed",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) => seedAcres(productUnitsType)(acresPerUnit, userQuantity),
    ],
    [
      () => keyword === "standard",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) =>
        productAcres(productUnitsType)(
          unitOfMeasure,
          unitsPerAcre,
          userQuantity
        ),
    ],
  ]);
}

function standardizedVolume(keyword) {
  return R.cond([
    [
      () => keyword === "cases",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) => caseVolume(productUnitsType)(unitsPerAcre, userQuantity),
    ],
    [
      () => keyword === "seed",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) => seedVolume(productUnitsType)(acresPerUnit, userQuantity),
    ],
    [
      () => keyword === "standard",
      (
        productUnitsType,
        unitOfMeasure,
        unitsPerAcre,
        acresPerUnit,
        userQuantity
      ) =>
        productVolume(productUnitsType)(
          unitOfMeasure,
          unitsPerAcre,
          userQuantity
        ),
    ],
  ]);
}

// gets only product variables necessary for calculations, normalizes "incomplete" products
function destructureProduct(product, productUnitsType) {
  if (product && hasSelection(product)) {
    var userQuantity = Number(product._userQuantity) || 0;
    var unitsPerAcre = product.match_rate || null;
    var acresPerUnit = product.acres_per_unit || null;
    var unitOfMeasure = product.unit_of_measure;
    var productType = product.product_type;
    var excluded = product.excluded;
    var title = product.title || "";

    var conversionKeyword =
      productType === "seed" ||
      (unitOfMeasure === "seed lbs" && productType === "additional")
        ? "seed"
        : unitOfMeasure === "cases"
        ? "cases"
        : "standard";

    var acres = standardizedAcreage(conversionKeyword)(
      productUnitsType,
      unitOfMeasure,
      unitsPerAcre,
      acresPerUnit,
      userQuantity
    );
    var volume = standardizedVolume(conversionKeyword)(
      productUnitsType,
      unitOfMeasure,
      unitsPerAcre,
      acresPerUnit,
      userQuantity
    );

    return {
      id: product._id,
      productType: productType,
      unitOfMeasure: unitOfMeasure,
      unitsPerAcre: unitsPerAcre,
      acres: acres,
      volume: volume,
      excluded: excluded,
      title: title,
    };
  } else {
    return {
      productType: R.prop("product_type", product)
        ? R.prop("product_type", product)
        : "",
      unitOfMeasure: R.has("unit_of_measure", product)
        ? product.unit_of_measure
        : "fl oz",
      unitsPerAcre: 0,
      acres: 0,
      volume: 0,
      excluded: false,
      title: title,
    };
  }
}

/*
PDF
*/

function generatePdf(state) {
  // hopefully stop an inconsistent bug where it was trying to get the pdf before the state existed
  var stateInitialized = (appState) => {
    return (
      !R.isEmpty(appState.productUnitsType) &&
      Array.isArray(appState.step1Seeds) &&
      Array.isArray(appState.step1Herbicides) &&
      Array.isArray(appState.step1Fungicides) &&
      Array.isArray(appState.step1Insecticides) &&
      Array.isArray(appState.step1Nematicides) &&
      Array.isArray(appState.step2Roundup) &&
      Array.isArray(appState.step2Xtendimax) &&
      Array.isArray(appState.step3Products) &&
      Array.isArray(appState.step4Products)
    );
  };

  const getRewardRates = (item) => {
    switch (item.productType) {
      case "seed":
      case "herbicide":
      case "fungicide":
      case "insecticide":
      case "nematicide":
        var allStep1Products = JSON.parse(JSON.stringify(step1Products(state)));

        var destructured = R.compose(
          R.filter((x) => R.prop("excluded", x) === false && x.acres),
          R.map((item) => {
            if (R.prop("product_type", item)) {
              return destructureProduct(item, state.productUnitsType);
            } else if (item[0] && item[1]) {
              var prodA = destructureProduct(item[0], state.productUnitsType);
              var prodB = destructureProduct(item[1], state.productUnitsType);
              var pairedAcres = prodA.acres + prodB.acres;
              prodA.acres = pairedAcres;
              prodB.acres = pairedAcres;
              return R.reduce(
                (prev, curr) =>
                  prev.acres > curr.acres
                    ? prev
                    : prev.acres === curr.acres
                    ? prev
                    : curr,
                {}
              )([prodA, prodB]);
            }
          })
        )(allStep1Products);

        // combine all seed acreage values (seed should be treated as one "product")
        var seedAcreage = R.sum(
          R.map(
            (item) => {
              return item.acres;
            },
            R.filter((item) => {
              return item.productType === "seed";
            }, destructured)
          )
        );

        // add the seed acreage as a single entity back to the non-seed product array
        var calcArray = R.append(
          {
            productType: "seed",
            acres: seedAcreage,
          },
          R.filter((item) => {
            return item.productType !== "seed";
          }, destructured)
        );

        // sort highest to lowest acreage
        var sorted = R.sort(R.descend(R.prop("acres")))(calcArray);

        //incentive portfolio rewards rates for 2024 
        if (state.year === "2024") {
          if (item.productType === "seed") {
            return R.compose((idx) => {
              if (idx === -1) {
                return;
              }
              const rate =
                idx === 0
                  ? "Qualifying Product"
                  : idx === 1
                  ? `$2/acre`
                  : idx === 2
                  ? `$3/acre`
                  : idx === 3
                  ? `$4/acre`
                  : idx === 4
                  ? "$5/acre"
                  : idx === 5
                  ? "$6/acre"
                  : "$0/acre";
              return rate;
            }, R.findIndex(R.propEq("productType", item.productType)))(sorted);
          } else {
            return R.compose((idx) => {
              var rate = "$0/acre";
              if (idx === -1) {
                const productTypeIdx = R.findIndex(
                  R.propEq("productType", item.productType),
                  sorted
                );
                rate =
                  productTypeIdx === 0
                    ? "Qualifying Product"
                    : productTypeIdx === 1
                    ? `$2/acre`
                    : productTypeIdx === 2
                    ? `$3/acre`
                    : productTypeIdx === 3
                    ? `$4/acre`
                    : productTypeIdx === 4
                    ? "$5/acre"
                    : productTypeIdx === 5
                    ? "$6/acre"
                    : "$0/acre";
              } else {
                rate =
                  idx === 0
                    ? "Qualifying Product"
                    : idx === 1
                    ? `$2/acre`
                    : idx === 2
                    ? `$3/acre`
                    : idx === 3
                    ? `$4/acre`
                    : idx === 4
                    ? "$5/acre"
                    : idx === 5
                    ? "$6/acre"
                    : "$0/acre";
              }
              return rate;
            }, R.findIndex(R.propEq("id", item.id)))(sorted);
          }
        }

        // incentive portfolio rewards rates for 2025 
        if (state.year === "2025") {
          if (item.productType === "seed") {
            return R.compose((idx) => {
              if (idx === -1) {
                return;
              }
              const rate =
                idx === 0
                  ? "Qualifying Product"
                  : idx === 1
                  ? `$2/acre`
                  : idx === 2
                  ? `$3/acre`
                  : idx === 3
                  ? `$4/acre`
                  : idx === 4
                  ? "$5/acre"
                  : "$0/acre";
              return rate;
            }, R.findIndex(R.propEq("productType", item.productType)))(sorted);
          } else {
            return R.compose((idx) => {
              var rate = "$0/acre";
              if (idx === -1) {
                const productTypeIdx = R.findIndex(
                  R.propEq("productType", item.productType),
                  sorted
                );
                rate =
                  productTypeIdx === 0
                    ? "Qualifying Product"
                    : productTypeIdx === 1
                    ? `$2/acre`
                    : productTypeIdx === 2
                    ? `$3/acre`
                    : productTypeIdx === 3
                    ? `$4/acre`
                    : productTypeIdx === 4
                    ? "$5/acre"
                    : "$0/acre";
              } else {
                rate =
                  idx === 0
                    ? "Qualifying Product"
                    : idx === 1
                    ? `$2/acre`
                    : idx === 2
                    ? `$3/acre`
                    : idx === 3
                    ? `$4/acre`
                    : idx === 4
                    ? "$5/acre"
                    : "$0/acre";
              }
              return rate;
            }, R.findIndex(R.propEq("id", item.id)))(sorted);
          }
        }
        

      case "roundup":
        if (herbicidesSelected(state)) {
          return "$1/acre";
        } else {
          return "$0/acre";
        }
      case "xtendimax":
        if (herbicidesSelected(state)) {
          return "$1/acre";
        } else {
          return "$0/acre";
        }
      case "additional":
        return "$0.50/acre";
      case "partner":
        if (state.year === "2024") {
          return "$2/acre";
        } else if (state.year === "2025") {
          if (item.title.includes("fierce")) {
            return "$2/acre";
          } else {
            return "$1/acre";
          }
        } else {
          return "$2/acre";
        }
      case "":
        return `$${roundupSelectedWithQuantity(state) ? 3 : 2}/acre`;
      default:
        return;
    }
  };

  const getRewardAmount = (item) => {
    switch (item.productType) {
      case "seed":
      case "herbicide":
      case "fungicide":
      case "insecticide":
      case "nematicide":
        var allStep1Products = JSON.parse(JSON.stringify(step1Products(state)));

        var destructured = R.compose(
          R.filter((x) => R.prop("excluded", x) === false && x.acres),
          R.map((item) => {
            if (R.prop("product_type", item)) {
              return destructureProduct(item, state.productUnitsType);
            } else if (item[0] && item[1]) {
              var prodA = destructureProduct(item[0], state.productUnitsType);
              var prodB = destructureProduct(item[1], state.productUnitsType);
              var pairedAcres = prodA.acres + prodB.acres;
              prodA.acres = pairedAcres;
              prodB.acres = pairedAcres;
              return R.reduce(
                (prev, curr) =>
                  prev.acres > curr.acres
                    ? prev
                    : prev.acres === curr.acres
                    ? prev
                    : curr,
                {}
              )([prodA, prodB]);
            }
          })
        )(allStep1Products);

        // combine all seed acreage values (seed should be treated as one "product")
        var seedAcreage = R.sum(
          R.map(
            (item) => {
              return item.acres;
            },
            R.filter((item) => {
              return item.productType === "seed";
            }, destructured)
          )
        );

        // add the seed acreage as a single entity back to the non-seed product array
        var calcArray = R.append(
          {
            productType: "seed",
            acres: seedAcreage,
          },
          R.filter((item) => {
            return item.productType !== "seed";
          }, destructured)
        );

        // sort highest to lowest acreage
        var sorted = R.sort(R.descend(R.prop("acres")))(calcArray);

        //rewards amounts for 2024 portfolio incentive
        if (state.year === "2024") {
          if (item.productType === "seed") {
            return R.compose((idx) => {
              if (idx === -1) {
                return 0;
              }
              const rate =
                idx === 0 ? -1
                  : idx === 1 ? 2 * item.acres //$2/acre
                  : idx === 2 ? 3 * item.acres //$3/acre
                  : idx === 3 ? 4 * item.acres //$4/acre
                  : idx === 4 ? 5 * item.acres //$5/acre
                  : idx === 5 ? 6 * item.acres //$6/acre
                  : 0;
              return rate;
            }, R.findIndex(R.propEq("productType", item.productType)))(sorted);
          } else {
            return R.compose((idx) => {
              var rate = 0;
              if (idx === -1) {
                const productTypeIdx = R.findIndex(
                  R.propEq("productType", item.productType),
                  sorted
                );
                rate =
                  productTypeIdx === 0 ? -1
                    : productTypeIdx === 1 ? 2 * item.acres //$2/acre
                    : productTypeIdx === 2 ? 3 * item.acres //$3/acre
                    : productTypeIdx === 3 ? 4 * item.acres //$4/acre
                    : productTypeIdx === 4 ? 5 * item.acres //$5/acre
                    : productTypeIdx === 5 ? 6 * item.acres //$6/acre
                    : 0;
              } else {
                rate =
                  idx === 0 ? -1
                    : idx === 1 ? 2 * item.acres //$2/acre
                    : idx === 2 ? 3 * item.acres //$3/acre
                    : idx === 3 ? 4 * item.acres //$4/acre
                    : idx === 4 ? 5 * item.acres //$5/acre
                    : idx === 5 ? 6 * item.acres //$6/acre
                    : 0;
              }
  
              return rate;
            }, R.findIndex(R.propEq("id", item.id)))(sorted);
          }
        }

        //rewards amounts for 2025 portfolio incentive
        if (state.year === "2025") {
          if (item.productType === "seed") {
            return R.compose((idx) => {
              if (idx === -1) {
                return 0;
              }
              const rate =
                idx === 0 ? -1
                  : idx === 1 ? 2 * item.acres //$2/acre
                  : idx === 2 ? 3 * item.acres //$3/acre
                  : idx === 3 ? 4 * item.acres //$4/acre
                  : idx === 4 ? 5 * item.acres //$5/acre
                  : 0;
              return rate;
            }, R.findIndex(R.propEq("productType", item.productType)))(sorted);
          } else {
            return R.compose((idx) => {
              var rate = 0;
              if (idx === -1) {
                const productTypeIdx = R.findIndex(
                  R.propEq("productType", item.productType),
                  sorted
                );
                rate =
                  productTypeIdx === 0 ? -1
                    : productTypeIdx === 1 ? 2 * item.acres //$2/acre
                    : productTypeIdx === 2 ? 3 * item.acres //$3/acre
                    : productTypeIdx === 3 ? 4 * item.acres //$4/acre
                    : productTypeIdx === 4 ? 5 * item.acres //$5/acre
                    : 0;
              } else {
                rate =
                  idx === 0 ? -1
                    : idx === 1 ? 2 * item.acres //$2/acre
                    : idx === 2 ? 3 * item.acres //$3/acre
                    : idx === 3 ? 4 * item.acres //$4/acre
                    : idx === 4 ? 5 * item.acres //$5/acre
                    : 0;
              }
  
              return rate;
            }, R.findIndex(R.propEq("id", item.id)))(sorted);
          }
        }
       
      case "roundup":
        if (herbicidesSelected(state)) {
          return item.acres; //$1/acre
        } else {
          return 0; //$0/acre
        }
      case "xtendimax":
        if (herbicidesSelected(state)) {
          return item.acres; //$1/acre
        } else {
          return 0; //$0/acre
        }
      case "additional":
        return .5 * item.acres; //$0.50/acre
      case "partner":
        if (state.year === "2024") {
          return 2 * item.acres //$2/acre`;
        } else if (state.year === "2025") {
          if (item.title.includes("fierce")) {
            return 2 * item.acres //$2/acre`;
          } else {
            return item.acres //$1/acre`;
          }
        } else {
          return 2 * item.acres //$2/acre`;
        }
      default:
        return 0;
    }
  };

  var formatCollection = (items, productUnitsType) => {
    var allProducts = R.map(
      (item) => {
        var product = destructureProduct(item, productUnitsType);

        var rewardAmount = getRewardAmount(product);

        return {
          display_name: nullString(item, "display_name"),
          acres: Number(product.acres.toFixed(2)),
          volume: formatOneDecimal(product.volume).toLocaleString(),
          rate:
            product.productType === "seed"
              ? formatSeedingRate(product.unitsPerAcre, product.unitOfMeasure)
              : `${product.unitsPerAcre} ${product.unitOfMeasure}`,
          unit_of_measure: product.unitOfMeasure,
          display_units:
            product.unitOfMeasure !== ""
              ? userQuantityDisplayUnits(
                  product.unitOfMeasure,
                  product.productType
                )()
              : "",
          reward_rate: getRewardRates(product),
          reward_amount: rewardAmount === -1 ? "Qualifying Product" : "$" + formatWholeNumber(rewardAmount).toLocaleString()
        };
      },
      R.filter((x) => {
        return x["excluded"] === false;
      }, items)
    );

    // only return valid products with quantity
    return R.filter((item) => {
      return item.display_name && item.acres && item.volume;
    }, allProducts);
  };

  var formatPairCollection = (items, productUnitsType) => {
    var allProducts = R.compose(
      // R.filter(item=>item.display_name && item.acres && item.volume),
      R.map((group) => formatCollection(group, productUnitsType)),
      R.values
    )(items);

    // only return valid products with quantity
    return allProducts;
  };

  var getAllHerbicides = (step1Herbicides, step1PairHerbicides, year) => {
    var allHerbicides = step1Herbicides;
    return allHerbicides;
  };

  if (stateInitialized(state)) {
    var allStep1Products = R.flatten([
      state.step1Seeds,
      state.step1Herbicides,
      state.step1Fungicides,
      state.step1Insecticides,
      state.step1Nematicides,
    ]);

    var allPairProducts = R.compose(R.values)(
      R.mergeAll([
        state.step1PairSeeds,
        state.step1PairHerbicides,
        state.step1PairFungicides,
        state.step1PairInsecticides,
        state.step1PairNematicides,
      ])
    );

    allStep1Products = R.union(allStep1Products, allPairProducts);

    var portfolioTotal = roundValue(
      portfolioIncentive(state.productUnitsType, allStep1Products, state.year)
    );

    var roundupXtendTotal = roundValue(
      roundupIncentive(
        state.productUnitsType,
        state.step2Roundup,
        getAllHerbicides(
          state.step1Herbicides,
          state.step1PairHerbicides,
          state.year
        ),
        state.year
      ) +
        xtendimaxIncentive(
          state.productUnitsType,
          state.step2Xtendimax,
          getAllHerbicides(
            state.step1Herbicides,
            state.step1PairHerbicides,
            state.year
          ),
          state.step2Roundup,
          state.year
        )
    );

    var additionalTotal = roundValue(
      additionalIncentive(state.productUnitsType, state.step3Products)
    );

    var partnerProductsTotal = roundValue(
      partnerProductsIncentive(
        state.productUnitsType,
        allStep1Products,
        state.step2Roundup,
        state.step4Products,
        state.year
      )
    );

    var loyaltyRewards =
      state.loyaltyRewardsToRetailer > 0
        ? state.loyaltyRewardsToRetailer * 0.1
        : 0;
    var totalRewardsToRetailer =
      state.loyaltyRewardsToRetailer > 0
        ? state.loyaltyRewardsToRetailer + loyaltyRewards
        : 0;

    var seedIncentiveTotal = formatWholeNumber(
      roundValue(
        seedIncentive(state.productUnitsType, step1Products(state), state.year)
      )
    );
    var showSeedIncentive = seedIncentiveTotal === -1 || seedIncentiveTotal > 0;
    var seedIsQualifyingProduct =
      showSeedIncentive && seedIncentiveTotal === -1;

    var total =
      portfolioTotal +
      roundupXtendTotal +
      additionalTotal +
      partnerProductsTotal;
    var showLoyaltyRewardSummary =
      total >= 1500 && state.loyaltyRewardsToRetailer > 0;

    var totalRewardsVal =
      state.loyaltyRewardsToRetailer > 0
        ? total + loyaltyRewards
        : 0;

    var today = new Date();

    sortProducts(state.step1Seeds);
    //sortProducts(state.step1PairSeeds)
    sortProducts(state.step1Herbicides);
    //sortProducts(state.step1PairHerbicides)
    sortProducts(state.step1Fungicides);
    // sortProducts(state.step1PairFungicides)
    sortProducts(state.step1Insecticides);
    //sortProducts(state.step1PairInsecticides)
    sortProducts(state.step1Nematicides);
    //sortProducts(state.step1PairNematicides)
    sortProducts(state.step2Roundup);
    sortProducts(state.step3Products);
    sortProducts(state.step4Products);

    var data = {
      year: state.year,
      date: new Date(today.getTime())
        .toLocaleString("en-us", {
          month: "long",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/[^\x20-\x7E]+/g, ""),
      unit:
        state.productUnitsType[0].toUpperCase() +
        state.productUnitsType.substring(1),
      notes: state.comments,
      total: formatWholeNumber(total),
      min_met: total >= 1500 ? "true" : "false",
      portfolio: formatWholeNumber(portfolioTotal),
      roundup_xtendimax: formatWholeNumber(roundupXtendTotal),
      additional_tot: formatWholeNumber(additionalTotal),
      partner_product: formatWholeNumber(partnerProductsTotal),
      seeds: formatCollection(state.step1Seeds, state.productUnitsType),
      herbicides: formatCollection(
        state.step1Herbicides,
        state.productUnitsType
      ),
      fungicides: formatCollection(
        state.step1Fungicides,
        state.productUnitsType
      ),
      insecticides: formatCollection(
        state.step1Insecticides,
        state.productUnitsType
      ),
      nematicides: formatCollection(
        state.step1Nematicides,
        state.productUnitsType
      ),
      seeds_p: formatPairCollection(
        state.step1PairSeeds,
        state.productUnitsType
      ),
      herbicides_p: formatPairCollection(
        state.step1PairHerbicides,
        state.productUnitsType
      ),
      fungicides_p: formatPairCollection(
        state.step1PairFungicides,
        state.productUnitsType
      ),
      insecticides_p: formatPairCollection(
        state.step1PairInsecticides,
        state.productUnitsType
      ),
      nematicides_p: formatPairCollection(
        state.step1PairNematicides,
        state.productUnitsType
      ),
      roundup: formatCollection(state.step2Roundup, state.productUnitsType),
      xtendimax: formatCollection(state.step2Xtendimax, state.productUnitsType),
      additional: formatCollection(state.step3Products, state.productUnitsType),
      partner_products: formatCollection(
        state.step4Products,
        state.productUnitsType
      ),
      qualifyingProductType: getQualifyingProductType(state.productUnitsType, allStep1Products),
      addGreenSectionBottomPadding: isQualifyingProductLast(state.productUnitsType, allStep1Products),
      rewards: formatWholeNumber(state.loyaltyRewardsToRetailer),
      loyalty_rewards: formatWholeNumber(loyaltyRewards),
      total_rewards_val: formatWholeNumber(totalRewardsVal),
      total_to_retailer: formatWholeNumber(totalRewardsToRetailer),
      show_loyalty_sum: showLoyaltyRewardSummary,
    };

    if (showSeedIncentive) {
      data["seed"] = seedIncentiveTotal;
      data["show_seed"] = showSeedIncentive;
      data["seed_is_qp"] = seedIsQualifyingProduct;
    }

    if (data["nematicides_p"].length > 0) {
      data["addGreenSectionBottomPadding"] = true;
    }

    return data;
  } else {
    return {};
  }
}

function sortProducts(stepProducts) {
  if (stepProducts) {
    //Avoid failure of clicking print/download pdf while no actual products selected
    stepProducts.forEach((item) => {
      item.display_name = nullString(item, "display_name");
    });

    stepProducts.sort(function (a, b) {
      return (
        b._userQuantity - a._userQuantity ||
        a.display_name.localeCompare(b.display_name)
      );
    });
  }
}

function sortProductsList(productsList) {
  var excludedProducts = R.filter((x) => { return x["excluded"] === true; }, productsList);
  var includedProducts = R.filter((x) => {return x["excluded"] === false; }, productsList);
  
  return includedProducts.sort(function (a, b) {
     return a.display_name.localeCompare(b.display_name);
  }).concat(excludedProducts);
}

// get string of just number of step
function stepNumber(productType) {
  var step1 = ["seed", "herbicide", "fungicide", "insecticide", "nematicide"];
  var step2 = ["roundup", "xtendimax"];

  return R.contains(productType, step1)
    ? "1"
    : R.contains(productType, step2)
    ? "2"
    : "3";
}

function roundupXtendIncentive(state) {
  return R.sum([
    roundupIncentive(
      state.productUnitsType,
      state.step2Roundup,
      state.step1Herbicides
    ),
    xtendimaxIncentive(
      state.productUnitsType,
      state.step2Xtendimax,
      state.step1Herbicides,
      state.step2Roundup,
      state.year
    ),
  ]);
}

function onClickBack(stepNumber, incentiveData) {
  analytics.dataPush(
    analytics.constructObj("Click - Back", `Step ${stepNumber}`, incentiveData)
  );
}

function hasProducts(items) {
  const parsedItems = JSON.parse(JSON.stringify(items));

  if (Array.isArray(items)) {
    return parsedItems.length && parsedItems[0].hasOwnProperty("_userQuantity");
  } else {
    return (
      !R.isEmpty(parsedItems) &&
      R.flatten(R.values(parsedItems))[0].hasOwnProperty("_userQuantity")
    );
  }
}

function hasPairProducts(group) {
  return !R.isEmpty(group);
}

function findindex(str) {
  var num = /\d/;
  var nums = str.match(num);
  return str.indexOf(nums);
}

function nullString(item, property) {
  return item && item[property] ? item[property] : "";
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  step1Products,
  productSetAcres,
  seedIncentive,
  portfolioIncentive,
  toggleAccordion,
  titleCase,
  sum,
  format,
  formatOneDecimal,
  formatTwoDecimals,
  formatWholeNumber,
  formatWholeNumberToNumber,
  canBeCalculated,
  formatSeedingRate,
  destructureProduct,
  userQuantityDisplayUnits,
  sortProducts,
  sortProductsList,
  stepNumber,
  onClickBack,
  roundupIncentive,
  roundupXtendIncentive,
  xtendimaxIncentive,
  additionalIncentive,
  partnerProductIncentive,
  partnerProductsIncentive,
  step4Eligible,
  roundupIncentiveEligible,
  xtendimaxIncentiveEligible,
  additionalIncentiveEligible,
  partnerProductsIncentiveEligible,
  loyaltyRewardsToRetailer,
  loyaltyRewards,
  totalRewardsToRetailer,
  comments,
  pdfRequest,
  hasProducts,
  hasPairProducts,
  findindex,
};
